import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LiveHost } from '@/mocks/liveHosts';

interface HostLiveCardProps {
  host: LiveHost;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

export default function HostLiveCard({ host }: HostLiveCardProps) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const hostSlug = host.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  const handleEnter = useCallback(() => {
    navigate(`/host/${hostSlug}`);
  }, [navigate, hostSlug]);

  return (
    <article
      className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/80 hover:border-foreground-300/50 transition-all duration-300 cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleEnter}
    >
      {/* Photo Section */}
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={host.photoUrl}
          alt={host.name}
          className={`w-full h-full object-cover object-top transition-transform duration-500 ${isHovered ? 'scale-105' : 'scale-100'}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/10"></div>

        {/* LIVE Badge */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
          </span>
          <span className="text-[9px] font-extrabold text-background-50 tracking-widest uppercase bg-red-500/90 px-2 py-0.5 rounded-full backdrop-blur-sm">
            AL AIRE
          </span>
        </div>

        {/* Role Badge */}
        <div className="absolute bottom-3 left-3 right-3">
          <p className="text-[10px] font-bold text-background-50/90 bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-full inline-block">
            {host.role}
          </p>
        </div>
      </div>

      {/* Info Section */}
      <div className="p-4">
        {/* Name */}
        <h3 className="text-base font-extrabold text-foreground-950 leading-tight mb-1 group-hover:text-primary-600 transition-colors line-clamp-1">
          {host.name}
        </h3>

        {/* Program */}
        <div className="flex items-center gap-1.5 mb-1">
          <div className="w-1 h-3.5 rounded-full bg-primary-500 shrink-0"></div>
          <p className="text-[11px] font-semibold text-foreground-600 truncate">{host.programName}</p>
        </div>

        {/* Radio */}
        <div className="flex items-center gap-1.5 mb-3">
          <i className="ri-radio-line text-[11px] text-foreground-400 shrink-0 w-3.5 h-3.5 flex items-center justify-center"></i>
          <span className="text-[11px] font-medium text-foreground-400 truncate">
            {host.radioName} · {host.radioFrequency}
          </span>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 pt-3 border-t border-background-200/60">
          <div className="flex items-center gap-1.5">
            <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
              <i className="ri-group-line text-xs text-foreground-500"></i>
            </div>
            <div>
              <p className="text-xs font-extrabold text-foreground-950 leading-none">{formatNumber(host.followers)}</p>
              <p className="text-[9px] font-semibold text-foreground-400">Seguidores</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
              <i className="ri-flashlight-line text-xs text-foreground-500"></i>
            </div>
            <div>
              <p className="text-xs font-extrabold text-foreground-950 leading-none">{host.activityScore}</p>
              <p className="text-[9px] font-semibold text-foreground-400">Actividad</p>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}